// FILE: src/services/telemetryService.js
//
// Reusable, privacy-conscious telemetry framework for PM Pixel.
//
// Design goals (see PLAN-telemetry.md):
//   • Fire-and-forget. A telemetry failure NEVER throws and NEVER blocks a
//     learner — every public method swallows its own errors.
//   • Anonymous-first. Signed-in users are attributed by auth user_id; everyone
//     else by a random localStorage `anon_id` (no PII).
//   • Complements, does not duplicate, existing analytics. This layer records
//     platform usage / engagement / conversion / technical-health signals only —
//     question-level and scoring data stay in their own tables.
//   • Excludes automated runs via `source` ('user' | 'test'), exactly like
//     sessionService / question_attempts.
//
// Writes go to the `telemetry_events` table (migration 026). Reads (the admin
// dashboard) go through the SECURITY DEFINER RPCs, also in 026.

import { supabase } from './supabaseClient';
import releases from '../data/releases.json';

// ── Constants ──────────────────────────────────────────────────────────────

const ANON_ID_KEY     = 'pmpixel_anon_id';
const APP_SESSION_KEY = 'pmpixel_app_session';
const MAX_BATCH       = 20;     // flush when the queue reaches this many events
const FLUSH_MS        = 5000;   // …or this long after the first queued event
const SLOW_QUERY_MS   = 2000;   // emit 'slow_query' when a timed read exceeds this
const MAX_STR         = 200;    // metadata string values are truncated to this

// ── Error-message deny-list ────────────────────────────────────────────────
// Patterns that match KNOWN browser/extension false positives — events with a
// message_short matching any of these are dropped silently by trackError().
// This is not for swallowing real bugs; reach for the drill-down + Analyze
// pattern first to confirm something is genuinely noise before adding it here.
//
// Notes per pattern:
//   • SafeLinks  — Microsoft Office / Outlook SafeLinks rewriter injects an
//     inline script that throws `Object Not Found Matching Id:N, MethodName:update`
//     on every page load for affected Office installs. Not a PM Pixel bug.
//     (Identified via the Errors drill-down + Analyze on 2026-05-31.)
//   • "Script error." — the browser's CORS-masked message for an exception in
//     a cross-origin script. Our CRA bundles are same-origin on Netlify, so
//     our own code can never produce this; it is extension/injected-script
//     noise. 5 events / 4 actors / 6 weeks, view:null, all app versions.
//     (Confirmed via drill-down + Analyze + prod query on 2026-07-22.)
//   • "Lock was stolen by another request" — @supabase/auth-js Web Locks
//     token-refresh race when a second tab grabs navigator.locks first; the
//     library recovers by design, but the loser surfaces as an unhandled
//     rejection. One 3-event single-user burst on 2026-06-28, none since.
//     (Confirmed 2026-07-22.)
//   • ResizeObserver loop — Chromium reports this informational condition as
//     an error; a layout change couldn't be delivered in the same frame.
//     Universally documented as ignorable noise. (Confirmed 2026-07-22.)
//
// Deliberately NOT denylisted: "Load failed" (dynamic import / asset fetch
// rejection). The 2026-07-16 occurrence was a single-user single-millisecond
// burst that never recurred, but a recurrence would be a REAL signal (CDN or
// code-split chunk failure) and must stay visible in the error tile.
// Browser-extension noise (2026-07-31 burst — one Firefox actor, stale
// 2026-07-27 bundle; triaged 2026-08-05):
//   • window.__firefox__ — Firefox reader-mode/extension shim injected before
//     the app loads; references its own missing global.
//   • window.ethereum — crypto-wallet content scripts (MetaMask et al.)
//     dereferencing their own injected global.
// Both are extension-injected scripts caught by the global handlers; PM Pixel
// code is not involved.
// Browser-extension noise (2026-09-14 re-triage of the 61 "degraded" dial):
//   • n.standardSelectors — 17 events / 1 Safari actor / 2026-08-26 → 09-10,
//     straddling app_versions 2026-08-19 AND 2026-09-09. Verified absent from
//     EVERY shipped prod asset (main.f153c629.js + chunks 451/505/721/899), so
//     no PM Pixel bundle has ever contained the symbol. The minified `n.` and
//     the "undefined is not an object" phrasing are a Safari content script
//     dereferencing its own uninjected config object.
//   • "No response received." — 1 event / 1 actor / 2026-09-05. Same class:
//     an extension messaging channel whose background page never answered.
//     Also absent from every prod asset.
// Both were single-actor and cost 36 of the 39 points the dial had lost.
export const ERROR_MESSAGE_DENYLIST = [
  /Object Not Found Matching Id:\d+,\s*MethodName:update/i,
  /^Script error\.?$/i,
  /^Lock was stolen by another request$/i,
  /^ResizeObserver loop (completed with undelivered notifications|limit exceeded)/i,
  /\b__firefox__\b/i,
  /window\.ethereum/i,
  /\bstandardSelectors\b/i,
  /^No response received\.?$/i,
];

// Script schemes that can only belong to a browser extension. Anything thrown
// from one of these is, by definition, not PM Pixel code.
//
// The denylist above is reactive — it needs a human to triage a cluster and add
// a pattern, which is why `standardSelectors` reported for 19 days. This guard
// is the structural half: it drops extension errors on sight, whatever they say.
// It is deliberately narrow. A frame must POSITIVELY match an extension scheme,
// and the error must contain NO frame from our own origin. An error with no
// stack, or a stack with no recognizable URLs (dev builds, webpack-internal:,
// minified Safari traces), is KEPT — when in doubt the error stays visible.
const EXTENSION_URL_RE =
  /\b(?:chrome-extension|moz-extension|safari-web-extension|safari-extension|ms-browser-extension|webkit-masked-url):\/\//i;

/**
 * True when an error demonstrably originated in an injected extension script.
 *
 * @param {*} error        the thrown value / rejection reason
 * @param {object} context trackError context; `filename` comes from ErrorEvent
 * @returns {boolean}
 */
export function isExtensionScriptError(error, context = {}) {
  try {
    // window.onerror hands us the offending script URL directly — the most
    // reliable signal available, so check it first.
    const filename = context && context.filename;
    if (typeof filename === 'string' && filename && EXTENSION_URL_RE.test(filename)) {
      return true;
    }

    const stack = error && typeof error.stack === 'string' ? error.stack : '';
    if (!stack || !EXTENSION_URL_RE.test(stack)) return false;

    // An extension frame is present. Keep the error anyway if OUR code is also
    // on the stack — an extension wrapping a real PM Pixel failure is a real
    // PM Pixel failure.
    const origin =
      (typeof window !== 'undefined' && window.location && window.location.origin) || '';
    if (origin && stack.includes(origin)) return false;

    return true;
  } catch (_) {
    return false; // never let the guard itself suppress an error
  }
}

const IS_TEST = process.env.NODE_ENV === 'test';

// Source mirrors exam_sessions/question_attempts. CI sets REACT_APP_SESSION_SOURCE='test'.
const SOURCE = process.env.REACT_APP_SESSION_SOURCE === 'test' ? 'test' : 'user';

// Current release version, auto-attached to every event so metrics can be
// compared before/after a release. releases.json is newest-first; fall back to
// an explicit env override, then 'unknown'.
const APP_VERSION =
  process.env.REACT_APP_RELEASE_VERSION ||
  (Array.isArray(releases) && releases[0] && releases[0].version) ||
  'unknown';

// Non-PII metadata keys that are allowed to reach the database. Anything else
// is stripped. NEVER includes answer text, question stems, email, or free text.
export const ALLOWED_METADATA_KEYS = [
  'view', 'exam_id', 'mode', 'cert', 'feature', 'target',
  'browser', 'device', 'os',
  'duration_ms', 'pages_viewed',
  'status', 'code', 'message_short',
  'from', 'to', 'had_anon_id',
  'app_version', 'release_version',
  'method', // signup/signin method: 'email' | 'google' | 'microsoft' | 'unknown'
  // Intelligent Pace Coaching (Phase 7) — anonymized pacing metrics.
  'pace_band', 'pace_finish_margin_min', 'pace_avg_seconds',
  // Case Study Question Framework (PR 3) — ids/sequence only, never content.
  'case_id', 'case_version', 'case_sequence', 'stage_id', 'stage_sequence',
  'section_id', 'artifact_id', 'question_type', 'attempt_id', 'score_pct',
  // Acquisition attribution (Content Factory Stage 2) — see the Acquisition
  // section below. Dimensions only, never PII / free text / full URLs.
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
  'referrer_host', 'landing_view',
  'ft_source', 'ft_medium', 'ft_campaign', 'ft_content', 'ft_term',
  'ft_referrer_host', 'ft_landing_view', 'ft_at',
  'content_id', 'content_cta', 'content_ref',
  // Activation observability (dormant-user Phase 1) — session-start funnel
  // dimensions only: entry route, pool size, and which loader satisfied the
  // start (supabase | bundled | pool). Never question content or PII.
  'kind', 'question_count', 'load_source', 'load_status',
  // Journey Activation onboarding (dark, journeyOnboardingEnabled) — funnel
  // dimensions only: which treatment the learner saw, which stage they
  // declared, and which recommendation they started. Never PII.
  'treatment', 'journey_stage',
  // Reopenable completed-session review — dimensions only: how old the attempt
  // was when it was reopened. Question stems, answers and explanations are
  // NEVER sent (the session id travels in exam_session_id, not in metadata).
  'session_age_days',
  // Admin dashboard load timing (2026-09-16) — millisecond splits + the slowest
  // RPC name only. Emitted for admins, who telemetry_daily already excludes.
  'data_ms', 'render_ms', 'request_count', 'slowest_target', 'slowest_ms',
  'server_ms', 'diagnosis',
];

// ── Module state ─────────────────────────────────────────────────────────────

let queue = [];
let flushTimer = null;
let initialized = false;
let currentUserId = null;     // cached from the auth subscription
let pageViewCount = 0;        // pages viewed this app session (for app_session_end)
let sessionStartedAt = null;  // ms epoch when this app session began
let lastViewName = null;      // most recent page_view name; attached to errors so bursts are traceable
let memoryAnonId = null;      // in-memory fallback when localStorage is blocked
let memoryAppSessionId = null;
// User id whose 'authenticated_open' has already been emitted for THIS app
// session. Identity resolution fires repeatedly (initial getSession, token
// refresh, and every tab refocus — see the Supabase auth-refocus note), so the
// event is deduped per user per app session and is an *open*, not a sign-in
// count. Null means nobody has been attributed yet this visit.
let authOpenEmittedFor = null;
let memoryFirstTouch = null;   // in-memory fallback for the first-touch record
let memorySessionTouch = null; // …and for the current-visit record

// ── Storage-safe helpers ─────────────────────────────────────────────────────

function safeGet(storage, key) {
  try {
    return typeof window !== 'undefined' && window[storage]
      ? window[storage].getItem(key)
      : null;
  } catch (_) {
    return null;
  }
}

function safeSet(storage, key, value) {
  try {
    if (typeof window !== 'undefined' && window[storage]) {
      window[storage].setItem(key, value);
    }
  } catch (_) {
    /* private mode / blocked storage — ignore */
  }
}

function uuid() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) { /* fall through */ }
  // RFC4122-ish fallback.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Stable per-browser anonymous id (localStorage). Non-PII. */
export function getAnonId({ createIfMissing = true } = {}) {
  let id = safeGet('localStorage', ANON_ID_KEY) || memoryAnonId;
  if (!id && createIfMissing) {
    id = uuid();
    memoryAnonId = id;
    safeSet('localStorage', ANON_ID_KEY, id);
  }
  return id || null;
}

/** Per-visit app-session id (sessionStorage). */
export function getAppSessionId() {
  let id = safeGet('sessionStorage', APP_SESSION_KEY) || memoryAppSessionId;
  if (!id) {
    id = uuid();
    memoryAppSessionId = id;
    safeSet('sessionStorage', APP_SESSION_KEY, id);
  }
  return id;
}

// ── Metadata sanitisation ────────────────────────────────────────────────────

/**
 * True only for values that are safe to put in the telemetry_events JSONB
 * column: finite numbers, booleans, and strings. Everything else — objects,
 * arrays, functions, symbols, and DOM nodes — is rejected.
 *
 * NaN and Infinity are excluded deliberately: JSON.stringify turns both into
 * null, so they would land in the DB as a silent hole rather than a number.
 */
function isScalarMetadataValue(val) {
  const type = typeof val;
  if (type === 'string' || type === 'boolean') return true;
  if (type === 'number') return Number.isFinite(val);
  return false;
}

/**
 * Reduce a caller's metadata to the allowlisted, JSON-safe subset.
 *
 * Keys are allowlisted (ALLOWED_METADATA_KEYS) so PII cannot leak. Values are
 * type-guarded for a different reason: an allowlisted key carrying a non-scalar
 * is a reliability hazard, not a privacy one. A live DOM node passed as, say,
 * `trackEvent('cta_click', 'usage', { target: e.target })` reaches the Supabase
 * insert, where JSON.stringify throws
 *   "Converting circular structure to JSON -> HTMLAnchorElement -> __reactFiber$..."
 * because React tags its rendered elements with a fiber back-reference. That
 * throw takes down the WHOLE batch, not just the offending event, so the value
 * type is guarded here rather than trusted at the call site.
 *
 * Rejected values are dropped silently and the rest of the event still ships —
 * telemetry never throws and never blocks a learner. (Hardened 2026-09-04 after
 * the 2026-08-09 circular-structure error group; no caller was passing a node
 * at the time, but nothing prevented one.)
 */
function sanitizeMetadata(meta) {
  const out = {};
  if (!meta || typeof meta !== 'object') return out;
  for (const key of ALLOWED_METADATA_KEYS) {
    const val = meta[key];
    if (val === undefined || val === null) continue;
    if (!isScalarMetadataValue(val)) continue;
    out[key] = typeof val === 'string' && val.length > MAX_STR ? val.slice(0, MAX_STR) : val;
  }
  return out;
}

// ── User-agent parsing (pure, testable) ──────────────────────────────────────

/** Best-effort, coarse {browser, device, os} from a UA string. No fingerprinting. */
export function normalizeUserAgent(ua) {
  const s = (ua || '').toLowerCase();
  let browser = 'unknown';
  if (s.includes('edg/') || s.includes('edge')) browser = 'Edge';
  else if (s.includes('chrome') && !s.includes('chromium')) browser = 'Chrome';
  else if (s.includes('firefox')) browser = 'Firefox';
  else if (s.includes('safari') && !s.includes('chrome')) browser = 'Safari';

  // Order matters: Apple mobile UAs contain "like Mac OS X", and Android UAs
  // contain "linux" — so check the more specific platforms first.
  let os = 'unknown';
  if (s.includes('windows')) os = 'Windows';
  else if (s.includes('iphone') || s.includes('ipad')) os = 'iOS';
  else if (s.includes('android')) os = 'Android';
  else if (s.includes('mac os') || s.includes('macintosh')) os = 'macOS';
  else if (s.includes('linux')) os = 'Linux';

  const device =
    s.includes('mobile') || s.includes('iphone') || s.includes('android')
      ? 'mobile'
      : (s.includes('ipad') || s.includes('tablet') ? 'tablet' : 'desktop');

  return { browser, device, os };
}

// ── Acquisition attribution (Content Factory Stage 2) ───────────────────────
// Conceptual model (approved 2026-08-10):
//   external acquisition → content item → CTA → app session → registration
// Three separate layers, never conflated:
//   • First-touch  — the source/medium/campaign/referrer that FIRST brought
//     this browser to PM Pixel. Written once per browser (localStorage, next
//     to pmpixel_anon_id) and echoed as ft_* on app_open and
//     register_completed. Recorded even for direct visits so "no record"
//     (pre-Stage-2 profile) is distinguishable from "arrived directly".
//   • Current visit — the same capture for THIS app session (sessionStorage),
//     attached as utm_* / referrer_host / landing_view on app_open.
//   • Internal content attribution — article pages link into the app with
//     ?content_id=…&content_cta=… (NEVER utm_*, so external acquisition is
//     not overwritten). Captured on app_open, then stripped from the URL via
//     replaceState (history.state preserved for the popstate handler — same
//     pattern as the ?preview strip in App.js).
// Only external referrers count: a same-host referrer (article → app) is
// dropped — that internal hop is exactly what content_id records.
//
// ?content_ref=<host> closes the resulting blind spot. An article page is
// same-origin with the app, so a Google → article → app visitor arrives with
// document.referrer pointing at our own article; the true external source was
// being lost and recorded as null. The article's beacon script forwards the
// host IT saw onto the app link as content_ref, which is used ONLY when there
// is no genuine external referrer on this hop — a real external referrer
// always wins, so content_ref can never overwrite a stronger signal.

const FIRST_TOUCH_KEY   = 'pmpixel_first_touch';
const SESSION_TOUCH_KEY = 'pmpixel_session_touch';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

/**
 * Pure parser for acquisition signals. `host` is the app's own hostname —
 * referrers on the same host are internal navigation, not acquisition.
 */
export function parseAcquisition({ search = '', referrer = '', host = '' } = {}) {
  const out = { utm: {}, content: {}, referrer_host: null };
  try {
    const params = new URLSearchParams(search || '');
    for (const key of UTM_KEYS) {
      const val = params.get(key);
      if (val) out.utm[key] = val;
    }
    const contentId = params.get('content_id');
    const contentCta = params.get('content_cta');
    if (contentId) out.content.content_id = contentId;
    if (contentCta) out.content.content_cta = contentCta;
    if (referrer) {
      try {
        const refHost = new URL(referrer).hostname;
        if (refHost && refHost !== host) out.referrer_host = refHost;
      } catch (_) { /* malformed referrer — ignore */ }
    }
    // Article-forwarded external referrer. Only fills the gap left by a
    // same-host (or missing) referrer; never overrides a real one.
    if (!out.referrer_host) {
      const contentRef = params.get('content_ref');
      if (contentRef && contentRef !== host) {
        out.referrer_host = contentRef;
        out.content.content_ref = contentRef;
      }
    }
  } catch (_) { /* never throw */ }
  return out;
}

function readStoredJson(storage, key, memoryValue) {
  const raw = safeGet(storage, key);
  if (raw) {
    try { return JSON.parse(raw); } catch (_) { /* corrupt — fall through */ }
  }
  return memoryValue || null;
}

/** The once-per-browser first-touch record, or null if never recorded. */
export function getFirstTouch() {
  return readStoredJson('localStorage', FIRST_TOUCH_KEY, memoryFirstTouch);
}

/**
 * ft_*-prefixed metadata for echoing first-touch acquisition on conversion
 * events (register_completed). Returns {} when no record exists, so callers
 * can spread it unconditionally.
 */
export function firstTouchMetadata() {
  const ft = getFirstTouch();
  if (!ft) return {};
  const out = {};
  if (ft.source)        out.ft_source = ft.source;
  if (ft.medium)        out.ft_medium = ft.medium;
  if (ft.campaign)      out.ft_campaign = ft.campaign;
  if (ft.content)       out.ft_content = ft.content;
  if (ft.term)          out.ft_term = ft.term;
  if (ft.referrer_host) out.ft_referrer_host = ft.referrer_host;
  if (ft.landing_view)  out.ft_landing_view = ft.landing_view;
  if (ft.at)            out.ft_at = ft.at;
  return out;
}

/** Remove internal-attribution params from the URL without a navigation. */
function stripQueryParams(keys) {
  try {
    if (typeof window === 'undefined' || !window.history || !window.history.replaceState) return;
    const url = new URL(window.location.href);
    let changed = false;
    for (const key of keys) {
      if (url.searchParams.has(key)) { url.searchParams.delete(key); changed = true; }
    }
    if (changed) window.history.replaceState(window.history.state, '', url.toString());
  } catch (_) { /* never throw */ }
}

/**
 * Capture acquisition state for this load and return the metadata to attach
 * to app_open. Persists the first-touch (once per browser) and current-visit
 * (once per app session) records. Fire-and-forget: never throws.
 */
export function captureAcquisition() {
  try {
    if (typeof window === 'undefined' || !window.location) return {};
    const loc = window.location;
    const parsed = parseAcquisition({
      search: loc.search,
      referrer: typeof document !== 'undefined' ? document.referrer : '',
      host: loc.hostname,
    });

    let landingView = 'home';
    try { landingView = new URLSearchParams(loc.search).get('view') || 'home'; } catch (_) { /* default */ }

    // Current-visit record: the first capture in this app session wins, so
    // reloads mid-visit report consistent values.
    let session = readStoredJson('sessionStorage', SESSION_TOUCH_KEY, memorySessionTouch);
    if (!session) {
      session = {
        source:        parsed.utm.utm_source || null,
        medium:        parsed.utm.utm_medium || null,
        campaign:      parsed.utm.utm_campaign || null,
        content:       parsed.utm.utm_content || null,
        term:          parsed.utm.utm_term || null,
        referrer_host: parsed.referrer_host || null,
        landing_view:  landingView,
      };
      memorySessionTouch = session;
      safeSet('sessionStorage', SESSION_TOUCH_KEY, JSON.stringify(session));
    }

    // First-touch record: written once per browser, ever.
    let first = readStoredJson('localStorage', FIRST_TOUCH_KEY, memoryFirstTouch);
    if (!first) {
      first = { ...session, at: new Date().toISOString().slice(0, 10) };
      memoryFirstTouch = first;
      safeSet('localStorage', FIRST_TOUCH_KEY, JSON.stringify(first));
    }

    // Internal content attribution: capture, then strip so the params never
    // leak into later pushState copies or bookmarks.
    const content = { ...parsed.content };
    if (content.content_id || content.content_cta || content.content_ref) {
      stripQueryParams(['content_id', 'content_cta', 'content_ref']);
    }

    const metadata = { landing_view: session.landing_view, ...content, ...firstTouchMetadata() };
    if (session.source)        metadata.utm_source = session.source;
    if (session.medium)        metadata.utm_medium = session.medium;
    if (session.campaign)      metadata.utm_campaign = session.campaign;
    if (session.content)       metadata.utm_content = session.content;
    if (session.term)          metadata.utm_term = session.term;
    if (session.referrer_host) metadata.referrer_host = session.referrer_host;
    return metadata;
  } catch (_) {
    return {};
  }
}

// ── Queue + flush ─────────────────────────────────────────────────────────────

function buildEvent(eventName, category, metadata, extra = {}) {
  return {
    event_name:      eventName,
    event_category:  category,
    user_id:         currentUserId || null,
    anon_id:         getAnonId(),
    app_session_id:  getAppSessionId(),
    exam_session_id: extra.examSessionId || null,
    metadata:        { app_version: APP_VERSION, ...sanitizeMetadata(metadata) },
    source:          SOURCE,
  };
}

function scheduleFlush() {
  if (IS_TEST) return;            // no lingering timers in unit tests
  if (flushTimer) return;
  flushTimer = setTimeout(() => { flushTelemetry(); }, FLUSH_MS);
}

function enqueue(event) {
  queue.push(event);
  if (queue.length >= MAX_BATCH) {
    flushTelemetry();
  } else {
    scheduleFlush();
  }
}

/**
 * Flush the queued events with a single bulk insert. Fire-and-forget: on
 * failure the batch is dropped (we never retry-storm) and the error is logged.
 * Returns a promise so tests can await it.
 */
export async function flushTelemetry() {
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  if (queue.length === 0) return;
  const batch = queue;
  queue = [];
  try {
    const { error } = await supabase.from('telemetry_events').insert(batch);
    if (error) console.warn('[telemetry] insert failed:', error.message);
  } catch (err) {
    console.warn('[telemetry] insert threw (non-fatal):', err?.message || err);
  }
}

// ── Public tracking API ───────────────────────────────────────────────────────

/** Generic event. category ∈ growth | usage | learning | technical. */
export function trackEvent(eventName, category = 'usage', metadata = {}, extra = {}) {
  try {
    if (!eventName) return;
    enqueue(buildEvent(eventName, category, metadata, extra));
  } catch (_) { /* never throw */ }
}

/** A navigation / page view. Counts toward this app session's pages_viewed. */
export function trackPageView(viewName, metadata = {}) {
  try {
    pageViewCount += 1;
    lastViewName = viewName || lastViewName;
    trackEvent('page_view', 'usage', { view: viewName, ...metadata });
  } catch (_) { /* never throw */ }
}

/** App/browser visit start. Emits 'app_open' with coarse browser/device/os. */
export function trackSessionStart(metadata = {}) {
  try {
    sessionStartedAt = Date.now();
    pageViewCount = 0;
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    trackEvent('app_open', 'usage', { ...normalizeUserAgent(ua), ...metadata });
  } catch (_) { /* never throw */ }
}

/**
 * Authenticated return signal. Emits 'authenticated_open' ONCE per app session
 * per user, and only ever with an identity already resolved.
 *
 * Why this exists rather than reusing 'app_open': initTelemetry() calls
 * trackSessionStart() synchronously while supabase.auth.getSession() is still
 * a pending promise, so every app_open row is built with currentUserId === null
 * and lands with a NULL user_id. That is not a data-quality wobble, it is
 * structural — 100% of app_open rows are unattributable — which silently made
 * any "did the learner come back?" metric keyed on app_open read zero forever.
 *
 * 'signin' is NOT a substitute: it fires only when credentials are exchanged,
 * so a learner returning with a live session (the common case for a nudge
 * recipient who never signed out) emits nothing. This event covers BOTH:
 *   • already-authenticated returns — resolved by the initial getSession()
 *   • newly-authenticated returns   — resolved by onAuthStateChange
 *
 * Fire-and-forget and never throws, like everything else in this module.
 */
export function trackAuthenticatedOpen(userId, metadata = {}) {
  try {
    if (!userId) return;                       // identity not resolved yet
    if (authOpenEmittedFor === userId) return; // already counted this visit
    authOpenEmittedFor = userId;
    // Attribute from the argument, not from module state. The entire failure
    // this event replaces was an event emitted before the cached id existed, so
    // this row must never depend on the cache having caught up.
    currentUserId = userId;
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    trackEvent('authenticated_open', 'growth', {
      ...normalizeUserAgent(ua),
      had_anon_id: Boolean(safeGet('localStorage', ANON_ID_KEY) || memoryAnonId),
      ...metadata,
    });
  } catch (_) { /* never throw */ }
}

/**
 * Single writer for the cached auth user id. Every identity-resolution path
 * routes through here so the authenticated-open signal can never drift out of
 * step with attribution. Signing out clears the dedupe latch so a subsequent
 * sign-in on the same visit is counted as a fresh authenticated open.
 */
function setCurrentUserId(userId) {
  const next = userId || null;
  currentUserId = next;
  if (!next) {
    authOpenEmittedFor = null;
    return;
  }
  trackAuthenticatedOpen(next);
}

/** App/browser visit end. Emits 'app_session_end' with duration + pages viewed. */
export function trackSessionComplete(metadata = {}) {
  try {
    const duration_ms = sessionStartedAt ? Math.max(0, Date.now() - sessionStartedAt) : undefined;
    trackEvent('app_session_end', 'usage', {
      duration_ms,
      pages_viewed: pageViewCount,
      ...metadata,
    });
  } catch (_) { /* never throw */ }
}

/** A caught/thrown error. metadata is kept tiny (truncated message + code). */
export function trackError(error, context = {}) {
  try {
    const message_short =
      (error && (error.message || String(error))) ? String(error.message || error) : 'unknown';
    // Deliberate cancellations are not defects: a fetch aborted because a
    // component unmounted (or a request was superseded) surfaces as an
    // AbortError unhandled rejection. Suppress those; real network failures
    // ("Load failed", "Failed to fetch") keep their distinct messages and
    // stay visible. (2026-07-26, from the "Load failed" burst analysis.)
    if (error && error.name === 'AbortError') return;
    if (/\baborted\b/i.test(message_short)) return;
    // Drop known-noise patterns (browser/extension false positives) silently —
    // see ERROR_MESSAGE_DENYLIST above for the inventory.
    if (ERROR_MESSAGE_DENYLIST.some((re) => re.test(message_short))) {
      return;
    }
    // Structural counterpart to the denylist: drop anything provably thrown by
    // an injected extension script, whatever its message. See the guard above.
    if (isExtensionScriptError(error, context)) {
      return;
    }
    trackEvent('error', 'technical', {
      message_short,
      // Attach the last-seen view so error bursts are traceable to a page
      // (historically view was null on all error events).
      view: context.view || lastViewName || undefined,
      ...context,
    });
  } catch (_) { /* never throw */ }
}

/** Record a timed read; emits 'slow_query' only when it exceeds the threshold. */
export function trackTiming(label, durationMs, metadata = {}) {
  try {
    if (typeof durationMs === 'number' && durationMs >= SLOW_QUERY_MS) {
      trackEvent('slow_query', 'technical', {
        target: label,
        duration_ms: Math.round(durationMs),
        // Attach the last-seen view (same as trackError) so slow reads are
        // traceable to a page — historically view was null on all slow rows.
        view: lastViewName || undefined,
        ...metadata,
      });
    }
  } catch (_) { /* never throw */ }
}

/**
 * Wrap an async read so a slow one emits 'slow_query'. Always returns the
 * underlying promise's result/rejection untouched — telemetry is side-effect only.
 */
export async function timeRead(label, fn, metadata = {}) {
  const start = Date.now();
  try {
    return await fn();
  } finally {
    trackTiming(label, Date.now() - start, metadata);
  }
}

// ── Lifecycle ─────────────────────────────────────────────────────────────────

/**
 * One-time setup. Called from src/index.js. Caches the auth user id, emits
 * 'app_open', and registers a flush + 'app_session_end' on page hide.
 */
export function initTelemetry() {
  if (initialized) return;
  initialized = true;

  // Cache the signed-in user id (anonymous = null) so events can be attributed
  // without an async call per event.
  try {
    // Seed from the LOCAL session (no network round-trip). getUser() would
    // hit the network and null out the id on any transient failure — the same
    // fault that mis-attributed exam sessions. onAuthStateChange keeps it fresh.
    supabase.auth.getSession().then(({ data }) => {
      // Already-authenticated return: the learner arrived with a live session.
      setCurrentUserId(data?.session?.user?.id || null);
    }).catch(() => { setCurrentUserId(null); });

    supabase.auth.onAuthStateChange((_event, session) => {
      // Newly-authenticated return (and token refresh / tab refocus, which the
      // per-user dedupe inside setCurrentUserId absorbs).
      setCurrentUserId(session?.user?.id || null);
    });
  } catch (_) { /* auth wiring optional */ }

  // Acquisition attribution rides on app_open (first-touch + current visit
  // + internal content dimensions). Never throws; {} when nothing to report.
  trackSessionStart(captureAcquisition());

  if (typeof document !== 'undefined' && document.addEventListener) {
    const onHide = () => {
      if (document.visibilityState === 'hidden') {
        trackSessionComplete();
        flushTelemetry();
      }
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', () => { trackSessionComplete(); flushTelemetry(); });
  }
}

// ── Dashboard read helpers (admin-gated RPCs) ───────────────────────────────────

function toISODate(d) {
  const dt = d instanceof Date ? d : new Date(d);
  return dt.toISOString().slice(0, 10);
}

export async function fetchActiveUsers(asOf = new Date()) {
  try {
    const { data, error } = await supabase.rpc('telemetry_active_users', { p_as_of: toISODate(asOf) });
    if (error) throw error;
    return data || null;
  } catch (err) {
    console.warn('[telemetry] fetchActiveUsers failed:', err?.message || err);
    return null;
  }
}

/**
 * Active Learners over [from, to]: distinct SIGNED-IN learners with >=1 real
 * (source='user') question attempt, admins excluded. Anonymous learners are
 * not counted (no stable per-person id). Returns the integer count, or null
 * on failure. Backed by the telemetry_active_learners RPC (migration 044).
 */
export async function fetchActiveLearners(from, to) {
  try {
    const { data, error } = await supabase.rpc('telemetry_active_learners', {
      p_from: toISODate(from), p_to: toISODate(to),
    });
    if (error) throw error;
    return data && Number.isFinite(data.active_learners) ? data.active_learners : null;
  } catch (err) {
    console.warn('[telemetry] fetchActiveLearners failed:', err?.message || err);
    return null;
  }
}

/**
 * Exam Ready learners as of a date: distinct learners whose latest readiness
 * snapshot is status='exam_ready' for at least one cert, admins excluded.
 * Returns the integer count, or null on failure. Backed by the
 * telemetry_exam_ready_learners RPC (migration 045).
 */
export async function fetchExamReadyLearners(asOf = new Date()) {
  try {
    const { data, error } = await supabase.rpc('telemetry_exam_ready_learners', {
      p_as_of: toISODate(asOf),
    });
    if (error) throw error;
    return data && Number.isFinite(data.exam_ready) ? data.exam_ready : null;
  } catch (err) {
    console.warn('[telemetry] fetchExamReadyLearners failed:', err?.message || err);
    return null;
  }
}

/**
 * Certified learners as of a date: distinct learners with a passed
 * learner_certifications row for any cert, admins excluded. Returns the
 * integer count, or null on failure. Backed by telemetry_certified_learners
 * (migration 046).
 */
export async function fetchCertifiedLearners(asOf = new Date()) {
  try {
    const { data, error } = await supabase.rpc('telemetry_certified_learners', {
      p_as_of: toISODate(asOf),
    });
    if (error) throw error;
    return data && Number.isFinite(data.certified) ? data.certified : null;
  } catch (err) {
    console.warn('[telemetry] fetchCertifiedLearners failed:', err?.message || err);
    return null;
  }
}

/**
 * Attribution / ownership health for [from, to]. Admin-gated. Surfaces:
 *   attributed_sessions, guest_sessions, orphaned_inconsistent_attempts (bug
 *   signal — target 0), attribution_failures (client save-time failures).
 * Backed by admin_attribution_health (migration 057). Returns the object or
 * null on failure.
 */
export async function fetchAttributionHealth(from, to) {
  try {
    const { data, error } = await supabase.rpc('admin_attribution_health', {
      p_from: toISODate(from), p_to: toISODate(to),
    });
    if (error) throw error;
    return data || null;
  } catch (err) {
    console.warn('[telemetry] fetchAttributionHealth failed:', err?.message || err);
    return null;
  }
}

export async function fetchDailyActive(from, to) {
  try {
    const { data, error } = await supabase.rpc('telemetry_daily_active', {
      p_from: toISODate(from), p_to: toISODate(to),
    });
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.warn('[telemetry] fetchDailyActive failed:', err?.message || err);
    return [];
  }
}

export async function fetchEventCounts(from, to, category = null) {
  try {
    const { data, error } = await supabase.rpc('telemetry_event_counts', {
      p_from: toISODate(from), p_to: toISODate(to), p_category: category,
    });
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.warn('[telemetry] fetchEventCounts failed:', err?.message || err);
    return [];
  }
}

export async function fetchMetadataBreakdown(from, to, eventName, metaKey) {
  try {
    const { data, error } = await supabase.rpc('telemetry_metadata_breakdown', {
      p_from: toISODate(from), p_to: toISODate(to),
      p_event_name: eventName, p_meta_key: metaKey,
    });
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.warn('[telemetry] fetchMetadataBreakdown failed:', err && err.message ? err.message : err);
    return [];
  }
}

export async function fetchEngagement(from, to) {
  try {
    const { data, error } = await supabase.rpc('telemetry_engagement', {
      p_from: toISODate(from), p_to: toISODate(to),
    });
    if (error) throw error;
    return data || null;
  } catch (err) {
    console.warn('[telemetry] fetchEngagement failed:', err?.message || err);
    return null;
  }
}

export async function fetchTechnicalHealth(from, to) {
  try {
    const { data, error } = await supabase.rpc('telemetry_technical_health', {
      p_from: toISODate(from), p_to: toISODate(to),
    });
    if (error) throw error;
    return data || null;
  } catch (err) {
    console.warn('[telemetry] fetchTechnicalHealth failed:', err?.message || err);
    return null;
  }
}

/**
 * Cross-learner health aggregates for Admin Mission Control (migration 084).
 * Returns { learners, avg_pct, readiness_dist, domains, low_success,
 * never_served } or null on failure. Admin-gated server-side; internal
 * traffic is excluded (same deny-list as the telemetry views).
 */
export async function fetchLearnerHealth(from, to) {
  try {
    const { data, error } = await supabase.rpc('admin_learner_health', {
      p_from: toISODate(from), p_to: toISODate(to),
    });
    if (error) throw error;
    return data || null;
  } catch (err) {
    console.warn('[telemetry] fetchLearnerHealth failed:', err?.message || err);
    return null;
  }
}

/**
 * Drill-down for the Errors tile. Returns { total, groups, rows } where
 * groups[] roll up by message_short and rows[] are the newest-first
 * per-occurrence list (capped server-side; see migration 030).
 */
export async function fetchErrorDetails(from, to, limit = 500) {
  try {
    const { data, error } = await supabase.rpc('telemetry_error_details', {
      p_from: toISODate(from), p_to: toISODate(to), p_limit: limit,
    });
    if (error) throw error;
    return data || { total: 0, groups: [], rows: [] };
  } catch (err) {
    console.warn('[telemetry] fetchErrorDetails failed:', err?.message || err);
    return { total: 0, groups: [], rows: [], _error: err?.message || String(err) };
  }
}

/**
 * Drill-down for the Slow queries tile. Same shape as fetchErrorDetails;
 * groups[] roll up by `target` and include avg/p95/max duration_ms so the
 * admin can see at a glance which read is consistently slow.
 */
export async function fetchSlowQueryDetails(from, to, limit = 500) {
  try {
    const { data, error } = await supabase.rpc('telemetry_slow_query_details', {
      p_from: toISODate(from), p_to: toISODate(to), p_limit: limit,
    });
    if (error) throw error;
    return data || { total: 0, groups: [], rows: [] };
  } catch (err) {
    console.warn('[telemetry] fetchSlowQueryDetails failed:', err?.message || err);
    return { total: 0, groups: [], rows: [], _error: err?.message || String(err) };
  }
}

/** Registered-users total — source of truth is learner_profiles, not telemetry. */
/**
 * Real registered accounts — the same population as the Registered Users
 * roster (non-anonymous auth users minus admins and the test deny-list).
 *
 * Previously this counted `learner_profiles` from the browser, where RLS only
 * returns the caller's own row, so the card read 1 for every admin. Returns
 * null (card shows —) if the RPC is unavailable, never a wrong number.
 */
export async function fetchRegisteredUsersCount() {
  try {
    const { data, error } = await supabase.rpc('telemetry_registered_accounts');
    if (error) throw error;
    const n = Number(data);
    return data !== null && data !== undefined && Number.isFinite(n) ? n : null;
  } catch (err) {
    console.warn('[telemetry] fetchRegisteredUsersCount failed:', err?.message || err);
    return null;
  }
}

/**
 * Admin-only diagnostic: database execution ms for each Telemetry dashboard
 * aggregate (migration 104). Used to split a slow load into network/API vs DB.
 * Returns null on failure.
 */
export async function fetchDashboardServerTimings(from, to) {
  try {
    const { data, error } = await supabase.rpc('telemetry_dashboard_server_timings', {
      p_from: toISODate(from),
      p_to: toISODate(to),
    });
    if (error) throw error;
    return data || null;
  } catch (err) {
    console.warn('[telemetry] fetchDashboardServerTimings failed:', err?.message || err);
    return null;
  }
}

// ── Pure dashboard calculations (DB-free; unit-tested) ──────────────────────────

/** Shape the active-users RPC payload + registered count into KPI tile values. */
export function deriveGrowthKpis(activeUsers, registeredCount) {
  const a = activeUsers || {};
  return {
    dau:        Number.isFinite(a.dau) ? a.dau : 0,
    wau:        Number.isFinite(a.wau) ? a.wau : 0,
    mau:        Number.isFinite(a.mau) ? a.mau : 0,
    registered: Number.isFinite(registeredCount) ? registeredCount : null,
  };
}

/**
 * Fill a daily-active result into a continuous series across [from, to], with
 * zeros on days that have no rows — so the trend chart has no gaps.
 */
export function toDailyActiveSeries(rows, from, to) {
  const byDay = new Map();
  for (const r of rows || []) {
    byDay.set(toISODate(r.day), {
      active_users: Number(r.active_users) || 0,
      events: Number(r.events) || 0,
    });
  }
  const out = [];
  const start = new Date(toISODate(from));
  const end = new Date(toISODate(to));
  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    const key = toISODate(d);
    const hit = byDay.get(key) || { active_users: 0, events: 0 };
    out.push({ day: key, active_users: hit.active_users, events: hit.events });
  }
  return out;
}

/** Bucket an array of per-actor session counts into frequency bands. */
export function bucketSessionFrequency(perActorCounts) {
  const buckets = { '1': 0, '2-3': 0, '4-6': 0, '7+': 0 };
  for (const n of perActorCounts || []) {
    if (n >= 7) buckets['7+'] += 1;
    else if (n >= 4) buckets['4-6'] += 1;
    else if (n >= 2) buckets['2-3'] += 1;
    else if (n >= 1) buckets['1'] += 1;
  }
  return buckets;
}

/** Returning-user ratio: actors active in >1 distinct day over the window. */
export function computeReturningRatio(activeByActorDays) {
  const entries = Object.values(activeByActorDays || {});
  if (entries.length === 0) return 0;
  const returning = entries.filter((days) => days > 1).length;
  return Math.round((returning / entries.length) * 100) / 100;
}

/** Conversion funnel counts from a list of growth-category event-count rows. */
export function deriveConversionFunnel(eventCountRows) {
  const get = (name) => {
    const row = (eventCountRows || []).find((r) => r.event_name === name);
    return row ? Number(row.event_count) || 0 : 0;
  };
  return {
    register_started:        get('register_started'),
    register_completed:      get('register_completed'),
    anonymous_to_registered: get('anonymous_to_registered'),
    signin:                  get('signin'),
    signin_failed:           get('signin_failed'),
  };
}

// ── Test utilities ──────────────────────────────────────────────────────────────

/** Reset module state between tests. Not used in production. */
export function _resetTelemetryForTests() {
  queue = [];
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  initialized = false;
  currentUserId = null;
  pageViewCount = 0;
  sessionStartedAt = null;
  lastViewName = null;
  memoryAnonId = null;
  memoryAppSessionId = null;
  authOpenEmittedFor = null;
  memoryFirstTouch = null;
  memorySessionTouch = null;
}

/** Inspect the pending queue in tests. */
export function _getQueueForTests() {
  return [...queue];
}
